import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { createChannel, updateChannel, deleteChannel } from '../ChannelsServices';
import { resolveBackendMessage } from '../../../Utils/apiMessages';

/**
 * Custom hook to handle channel CRUD actions
 * @param {Function} refetchChannels - Function to refetch channels after action
 * @param {Function} handleError - Error handler function
 * @returns {Object} Action handlers and loading states
 */
const useChannelActions = (refetchChannels, handleError) => {
    const { t } = useTranslation();
    const [createLoading, setCreateLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState({});
    const [deleteLoading, setDeleteLoading] = useState({});

    const handleCreate = useCallback(async (values, form = null) => {
        setCreateLoading(true);
        try {
            const payload = await createChannel(values);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            refetchChannels();
            return true;
        } catch (error) {
            handleError(error, form);
            return false;
        } finally {
            setCreateLoading(false);
        }
    }, [refetchChannels, handleError, t]);

    const handleUpdate = useCallback(async (id, values, form = null) => {
        setUpdateLoading(prev => ({ ...prev, [id]: true }));
        try {
            // Filter values to only include allowed fields per API
            const payload = {};
            if (values.agent_id !== undefined) payload.agent_id = values.agent_id;
            if (values.is_synchronization_enabled !== undefined) payload.is_synchronization_enabled = values.is_synchronization_enabled;
            if (values.credential_id !== undefined) payload.credential_id = values.credential_id;
            if (values.name !== undefined) payload.name = values.name;
            if (values.description !== undefined) payload.description = values.description;

            const channelPayload = await updateChannel(id, payload);
            const text = resolveBackendMessage(t, channelPayload);

            if (text) message.success(text);
            refetchChannels();
            return true;
        } catch (error) {
            handleError(error, form);
            return false;
        } finally {
            setUpdateLoading(prev => ({ ...prev, [id]: false }));
        }
    }, [refetchChannels, handleError, t]);

    const handleDelete = useCallback(async (id) => {
        setDeleteLoading(prev => ({ ...prev, [id]: true }));
        try {
            const payload = await deleteChannel(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            refetchChannels();
            return true;
        } catch (error) {
            handleError(error);
            return false;
        } finally {
            setDeleteLoading(prev => ({ ...prev, [id]: false }));
        }
    }, [refetchChannels, handleError, t]);

    return {
        handleCreate,
        handleUpdate,
        handleDelete,
        createLoading,
        updateLoading,
        deleteLoading,
    };
};

export default useChannelActions;
